import type { APIFieldAttributes } from '@ardoq/api-types';
import {
  getNumberFormat,
  numberFormatOptions,
  type Locale,
} from '@ardoq/locale';
import type { FormatNodeValue } from '../types';
import type { TreemapSizing } from './toSizing';
import { omit } from 'lodash';

/**
 * Compact, and to one decimal place.
 *
 * `formatNumber`'s own abbreviated form rounds to whole units, which these views cannot use:
 * Contact Center at 4,650,000 and Pricing and Billing at 5,400,000 both print as `5m`, so two
 * tiles a reader can see are different sizes carry the same number. One decimal keeps them
 * apart — `4.7m` and `5.4m` — and is still short enough to sit inside a tile.
 *
 * The field's own option underneath, so a cost field configured as USD still reads as money.
 * Its minimum comes off first: `Intl.NumberFormat` throws a `RangeError` when an explicit
 * minimum exceeds the maximum, which the Decimal format's two decimal places would.
 */
const toCompactOption = (formatKey: string): Intl.NumberFormatOptions => ({
  ...omit(
    numberFormatOptions.get(formatKey)?.option ?? {},
    'minimumFractionDigits'
  ),
  notation: 'compact',
  maximumFractionDigits: 1,
});

type ToFormatNodeValueArgs = {
  locale: Locale;
  /** The fields the view offers to size by, to find the selected one's own number format in. */
  numericFields: APIFieldAttributes[];
  /**
   * The sizing the hierarchy was summed with, not the setting it came from.
   *
   * `toSizing` refuses a name the view no longer offers and a confidential field it offers but
   * cannot read, and both refusals fall back to counting components. Asking `sizeByFieldName`
   * again here got a different answer: a confidential field is in `numericFields` — both
   * producers stamp the flag rather than dropping the field — so the lookup succeeded and every
   * label was written in the refused field's format. A capability holding six components read
   * `US$6`, which is not a formatting blemish but a count presented as an amount of money.
   */
  sizing: TreemapSizing;
};

/**
 * A node's value as a reader should see it. Values are drawn inside the shape they describe, so
 * length is the constraint: an unabbreviated `8900000` is wider than most tiles, and the label
 * has nowhere to put the overflow but another line.
 */
export const toFormatNodeValue = ({
  locale,
  numericFields,
  sizing,
}: ToFormatNodeValueArgs): FormatNodeValue => {
  const field =
    sizing.kind === 'field'
      ? numericFields.find(({ name }) => name === sizing.fieldName)
      : undefined;
  const format = getNumberFormat(
    toCompactOption(field?.numberFormatOptions ?? 'default'),
    locale
  );
  return value =>
    value === null || value === undefined || Number.isNaN(value)
      ? ''
      : format.format(value);
};
